import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Edit, Trash2, Repeat } from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import RouteMapModal from './RouteMapModal';
import { useWorkTracking } from './appointment/hooks/useWorkTracking';
import { API } from './appointment/utils';
import InfoPanel from './appointment/panels/InfoPanel';
import InstallationsPanel from './appointment/panels/InstallationsPanel';
import WorkTrackingPanel from './appointment/panels/WorkTrackingPanel';
import PerInstallationWorkTrackingPanel from './appointment/panels/PerInstallationWorkTrackingPanel';
import DocumentsPanel from './appointment/panels/DocumentsPanel';
import StatusUpdatePanel from './appointment/panels/StatusUpdatePanel';
import StartAddressModal from './appointment/panels/StartAddressModal';

const AppointmentDetailModal = ({ appointment, teams, onClose, onUpdate, onDocumentUpdate, onEdit, allAppointments = [], userNames = {} }) => {
  const { t } = useTranslation();
  const { user, canManageAppointments, isQualityControl, canStartStopWork, canEditStartAddress } = usePermissions();
  const [assignedInstaller, setAssignedInstaller] = useState(null);
  const [showMapModal, setShowMapModal] = useState(false);
  const [showCostModal, setShowCostModal] = useState(false);
  const [showStartAddressModal, setShowStartAddressModal] = useState(false);

  const team = teams.find((t) => t.id === appointment.team_id);
  const canTrackThisWork = canStartStopWork(appointment.assigned_installer_id);
  const canEditThisStartAddress = canEditStartAddress(appointment.assigned_installer_id);

  const workTracking = useWorkTracking(appointment, onUpdate, t);

  // Fetch assigned installer info
  useEffect(() => {
    if (appointment.assigned_installer_id) {
      axios.get(`${API}/users/${appointment.assigned_installer_id}`)
        .then(res => setAssignedInstaller(res.data))
        .catch(e => console.error("Error fetching installer:", e));
    }
  }, [appointment.assigned_installer_id]);

  const getStatusLabel = (status) => t(`appointment.statuses.${status}`) || t('appointment.statuses.scheduled');
  const getStatusBadgeClass = (status) => {
    const classes = {
      completed: "bg-green-100 text-green-800",
      not_completed: "bg-red-100 text-red-800",
      to_reschedule: "bg-yellow-100 text-yellow-800",
      confirmed: "bg-blue-100 text-blue-800",
      provisional: "bg-purple-100 text-purple-800",
    };
    return classes[status] || "bg-gray-100 text-gray-800";
  };

  const getStartAddress = () => {
    if (appointment.custom_start_address) return appointment.custom_start_address;
    if (allAppointments.length > 0 && appointment.assigned_installer_id) {
      const appointmentDate = new Date(appointment.scheduled_date).toISOString().split('T')[0];
      const sameDayAppointments = allAppointments
        .filter(apt => apt.assigned_installer_id === appointment.assigned_installer_id &&
          new Date(apt.scheduled_date).toISOString().split('T')[0] === appointmentDate)
        .sort((a, b) => a.scheduled_time.localeCompare(b.scheduled_time));
      const currentIndex = sameDayAppointments.findIndex(apt => apt.id === appointment.id);
      if (currentIndex > 0) {
        const prev = sameDayAppointments[currentIndex - 1];
        return `${prev.address}, ${prev.city}, ${prev.province}`;
      }
    }
    if (user?.role === "installer" && user?.starting_address) return user.starting_address;
    if (assignedInstaller?.starting_address) return assignedInstaller.starting_address;
    return null;
  };

  const isFirstAppointmentOfDay = () => {
    if (allAppointments.length === 0 || !appointment.assigned_installer_id) return true;
    const appointmentDate = new Date(appointment.scheduled_date).toISOString().split('T')[0];
    const sameDayAppointments = allAppointments
      .filter(apt => apt.assigned_installer_id === appointment.assigned_installer_id &&
        new Date(apt.scheduled_date).toISOString().split('T')[0] === appointmentDate)
      .sort((a, b) => a.scheduled_time.localeCompare(b.scheduled_time));
    return sameDayAppointments.length === 0 || sameDayAppointments[0].id === appointment.id;
  };

  const [deleteScopeDialog, setDeleteScopeDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [implicitSiblings, setImplicitSiblings] = useState([]);

  const performDelete = async (seriesScope) => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/appointments/${appointment.id}`, { params: { series_scope: seriesScope } });
      toast.success(t('appointment.messages.deleted'));
      setDeleteScopeDialog(false);
      setImplicitSiblings([]);
      onUpdate();
    } catch (e) {
      toast.error(t('appointment.messages.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  // Delete this appointment + a list of sibling ids (implicit series). Uses N DELETE calls.
  const performBulkDelete = async (siblingIds) => {
    setDeleting(true);
    try {
      const ids = [appointment.id, ...siblingIds];
      const results = await Promise.allSettled(
        ids.map(id => axios.delete(`${API}/appointments/${id}`, { params: { series_scope: 'this' } }))
      );
      const failed = results.filter(r => r.status === 'rejected').length;
      if (failed === 0) toast.success(`${ids.length} appuntamenti eliminati`);
      else toast.error(`${ids.length - failed} eliminati, ${failed} falliti`);
      setDeleteScopeDialog(false);
      setImplicitSiblings([]);
      onUpdate();
    } catch (e) {
      toast.error(t('appointment.messages.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  // Find "implicit series" siblings when appointment has no recurrence_group_id:
  // same installer + same client + same scheduled_time + same status + future date.
  // Helps operators clean up manually-duplicated provvisori (bug segnalato da Veronica).
  const findImplicitSiblings = () => {
    if (!appointment || !allAppointments || allAppointments.length === 0) return [];
    const key = {
      installer: appointment.assigned_installer_id,
      client: (appointment.client_name || '').trim().toLowerCase(),
      time: appointment.scheduled_time,
      status: appointment.status,
    };
    if (!key.installer || !key.client || !key.time) return [];
    return allAppointments.filter(a =>
      a.id !== appointment.id &&
      !a.recurrence_group_id && // only implicit siblings (real series is already handled by scope dialog)
      a.assigned_installer_id === key.installer &&
      (a.client_name || '').trim().toLowerCase() === key.client &&
      a.scheduled_time === key.time &&
      a.status === key.status &&
      a.scheduled_date >= appointment.scheduled_date
    ).sort((x, y) => x.scheduled_date.localeCompare(y.scheduled_date));
  };

  const handleDelete = async () => {
    // Serie ricorrente vera (recurrence_group_id presente) -> dialog 3 opzioni
    if (appointment.recurrence_group_id) {
      setImplicitSiblings([]);
      setDeleteScopeDialog(true);
      return;
    }
    // Rileva "serie implicita" (appuntamenti creati singolarmente ma identici)
    const siblings = findImplicitSiblings();
    if (siblings.length > 0) {
      setImplicitSiblings(siblings);
      setDeleteScopeDialog(true);
      return;
    }
    if (!window.confirm(t('appointment.messages.deleteConfirm'))) return;
    performDelete("this");
  };

  const startAddress = getStartAddress();
  const isFirstOfDay = isFirstAppointmentOfDay();

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onMouseDown={(e) => {
        // Chiude SOLO se il click parte direttamente sul backdrop, non su un figlio
        // (es. tooltip/popover/select nativi che propagano fino al parent).
        // Bug segnalato da Veronica 2026-07-03: modal si chiudeva copiando l'indirizzo.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-bold text-gray-800">{t('appointment.details')}</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
          </div>
          <div className="flex items-center gap-2">
            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(appointment.status)}`}>
              {getStatusLabel(appointment.status)}
            </span>
            {canEditThisStartAddress && (
              <button onClick={() => setShowStartAddressModal(true)}
                className="text-xs px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded text-gray-700">
                {t('common.edit') || 'Modifica'} partenza
              </button>
            )}
          </div>
        </div>

        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          <InfoPanel
            appointment={appointment} team={team}
            assignedInstaller={assignedInstaller} userNames={userNames}
            onNavigate={() => {
              // Apre direttamente Google Maps (app nativa su mobile, web su desktop).
              // Il pulsante "Naviga" deve LANCIARE la navigazione, non aprire una modale interna.
              const destination = encodeURIComponent(
                `${appointment.address}, ${appointment.city}${appointment.province ? ', ' + appointment.province : ''}, Italia`
              );
              const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${destination}&travelmode=driving`;
              window.open(mapsUrl, '_blank', 'noopener,noreferrer');
            }}
            onCalculateCosts={() => setShowCostModal(true)}
            startAddress={startAddress} isFirstOfDay={isFirstOfDay}
          />
          <InstallationsPanel appointment={appointment} />
          {user?.role === 'installer' ? (
            // Su richiesta Veronica 2026-07-03: rimuovere il tasto Inizio/Fine Lavoro dall'app
            // installatore per evitare confusione gestionale. L'esito viene validato dal
            // collaudo Salesforce automatico. Backoffice (admin/operator/QC) mantiene
            // la visibilità del pannello per gestire eventuali appuntamenti legacy.
            <div className="border-t pt-4 mt-4" data-testid="installer-sf-collaudo-info">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                <span className="text-blue-500 text-xl leading-none mt-0.5">ℹ️</span>
                <div className="text-sm text-blue-900">
                  <p className="font-semibold">Esito automatico via Salesforce</p>
                  <p className="text-blue-800 text-xs mt-0.5">
                    L&apos;esito di questo appuntamento sarà validato automaticamente
                    dal collaudo Salesforce. Non serve premere alcun pulsante di inizio/fine lavoro.
                  </p>
                </div>
              </div>
            </div>
          ) : (appointment.installations && appointment.installations.length > 1) ? (
            <PerInstallationWorkTrackingPanel
              appointment={appointment}
              canStartStopWork={canTrackThisWork}
              onRefresh={onDocumentUpdate || onUpdate}
              currentUser={user}
              t={t}
            />
          ) : (
            <WorkTrackingPanel appointment={appointment} workTracking={workTracking} canStartStopWork={canTrackThisWork} currentUser={user} onUpdate={onUpdate} />
          )}
          <DocumentsPanel appointment={appointment} canEdit={canManageAppointments} onDocumentUpdate={onDocumentUpdate} />
          <StatusUpdatePanel appointment={appointment} isQualityControl={isQualityControl} onUpdate={onUpdate} />
        </div>

        {canManageAppointments && !isQualityControl && (
          <div className="p-6 border-t border-gray-200 flex gap-3">
            <button onClick={onEdit}
              className="flex-1 flex items-center justify-center gap-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              data-testid="edit-appointment-btn">
              <Edit size={18} />{t('common.edit')}
            </button>
            <button onClick={handleDelete}
              className="flex items-center justify-center gap-2 py-2 px-4 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition"
              data-testid="delete-appointment-btn">
              <Trash2 size={18} />
            </button>
          </div>
        )}

        {isQualityControl && (
          <div className="p-4 border-t border-gray-200 bg-purple-50">
            <p className="text-sm text-purple-700 text-center">
              Modalita Quality Control - Solo visualizzazione
            </p>
          </div>
        )}
      </div>

      {/* Route Map Modals */}
      <RouteMapModal isOpen={showMapModal} onClose={() => setShowMapModal(false)}
        startAddress={startAddress}
        endAddress={`${appointment.address}, ${appointment.city}, ${appointment.province}, Italia`}
        startLabel={user?.role === "installer" ? "La tua posizione" : assignedInstaller ? `${assignedInstaller.first_name} ${assignedInstaller.last_name}` : "Partenza"}
        endLabel={appointment.client_name} showCosts={false} />
      <RouteMapModal isOpen={showCostModal} onClose={() => setShowCostModal(false)}
        startAddress={startAddress}
        endAddress={`${appointment.address}, ${appointment.city}, ${appointment.province}, Italia`}
        startLabel={user?.role === "installer" ? "La tua posizione" : assignedInstaller ? `${assignedInstaller.first_name} ${assignedInstaller.last_name}` : "Partenza"}
        endLabel={appointment.client_name} showCosts={true} />

      {showStartAddressModal && (
        <StartAddressModal appointment={appointment} isFirstOfDay={isFirstOfDay}
          onClose={() => setShowStartAddressModal(false)} onUpdate={onUpdate} />
      )}

      {/* Recurrence Delete Scope Dialog */}
      {deleteScopeDialog && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 z-[60] flex items-center justify-center p-4"
          data-testid="recurrence-delete-scope-dialog"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-200 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Repeat size={20} className="text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">
                {implicitSiblings.length > 0 ? 'Elimina appuntamenti simili' : 'Elimina appuntamento ricorrente'}
              </h3>
            </div>
            <div className="p-5">
              {implicitSiblings.length > 0 ? (
                <>
                  <p className="text-sm text-gray-600 mb-2">
                    Ho trovato <strong>{implicitSiblings.length}</strong> altri appuntamenti futuri con
                    stesso installatore, stesso cliente, stesso orario e stesso stato.
                  </p>
                  <p className="text-xs text-gray-500 mb-3">
                    Non sono legati come serie ricorrente (creati singolarmente) ma sembrano parte
                    della stessa pianificazione. Cosa vuoi fare?
                  </p>
                  <div className="mb-3 max-h-32 overflow-y-auto bg-gray-50 rounded p-2 border border-gray-200">
                    {implicitSiblings.slice(0, 8).map(s => (
                      <div key={s.id} className="text-xs text-gray-700 py-0.5" data-testid={`implicit-sibling-${s.id}`}>
                        • {new Date(s.scheduled_date).toLocaleDateString('it-IT')} {s.scheduled_time}
                      </div>
                    ))}
                    {implicitSiblings.length > 8 && (
                      <div className="text-xs text-gray-500 italic mt-0.5">
                        + altri {implicitSiblings.length - 8}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <button type="button" onClick={() => performDelete('this')} disabled={deleting}
                      className="w-full text-left px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-red-500 transition disabled:opacity-50"
                      data-testid="implicit-delete-only-this">
                      <div className="font-medium text-gray-800">Solo questo</div>
                      <div className="text-xs text-gray-500 mt-0.5">Elimina solo l&apos;appuntamento corrente</div>
                    </button>
                    <button type="button" onClick={() => performBulkDelete(implicitSiblings.map(s => s.id))} disabled={deleting}
                      className="w-full text-left px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-red-500 transition disabled:opacity-50"
                      data-testid="implicit-delete-all-similar">
                      <div className="font-medium text-gray-800">Questo + tutti i {implicitSiblings.length} simili</div>
                      <div className="text-xs text-gray-500 mt-0.5">Elimina tutti gli appuntamenti simili elencati sopra</div>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-4">
                    Questo appuntamento fa parte di una serie ricorrente. Quali vuoi eliminare?
                  </p>
                  <div className="space-y-2">
                    <button type="button" onClick={() => performDelete('this')} disabled={deleting}
                      className="w-full text-left px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-red-500 transition disabled:opacity-50"
                      data-testid="recurrence-delete-scope-this">
                      <div className="font-medium text-gray-800">Solo questo</div>
                      <div className="text-xs text-gray-500 mt-0.5">Elimina solo l&apos;appuntamento corrente</div>
                    </button>
                    <button type="button" onClick={() => performDelete('future')} disabled={deleting}
                      className="w-full text-left px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-red-500 transition disabled:opacity-50"
                      data-testid="recurrence-delete-scope-future">
                      <div className="font-medium text-gray-800">Questo e successivi</div>
                      <div className="text-xs text-gray-500 mt-0.5">Elimina questo appuntamento e tutti i successivi della serie</div>
                    </button>
                    <button type="button" onClick={() => performDelete('all')} disabled={deleting}
                      className="w-full text-left px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-red-500 transition disabled:opacity-50"
                      data-testid="recurrence-delete-scope-all">
                      <div className="font-medium text-gray-800">Tutta la serie</div>
                      <div className="text-xs text-gray-500 mt-0.5">Elimina tutti gli appuntamenti della serie ricorrente</div>
                    </button>
                  </div>
                </>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button type="button" onClick={() => { setDeleteScopeDialog(false); setImplicitSiblings([]); }} disabled={deleting}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
                data-testid="recurrence-delete-scope-cancel">
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentDetailModal;
